import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ContentSummary, ContentTypeDefinition, PublicationSummary } from '@cms/contracts'
import { ApiError } from '../api/client.js'
import { listContentTypes, listContents, listPublications } from '../api/endpoints.js'
import { absoluteDateTime, relativeTime } from '../domain/format.js'
import { displayDomain, liveUrl } from '../domain/live-url.js'
import { useI18n } from '../i18n/context.js'
import { paths, type SiteSection } from '../routes.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { siteStatusTone } from '../shell/SiteSwitcher.js'
import { Banner, Card, EmptyState, ExternalButton, Icon, LinkButton, PageHeader, Skeleton, StatusChip, TableSkeleton } from '../ui/primitives.js'
import { contentStatusTone, contentTypeIcon, publicationTone } from '../ui/status.js'
import type { IconName } from '../ui/icons.js'

type Status = ContentSummary['status']
const ACTIVE_STATUSES: Status[] = ['draft', 'review', 'published']
const STATUS_ICONS: Record<Status, IconName> = { draft: 'edit_note', review: 'visibility', published: 'check_circle', trashed: 'delete' }

const SHORTCUTS: { section: SiteSection; icon: IconName; titleKey: string; helpKey: string }[] = [
  { section: 'pagine', icon: 'web', titleKey: 'nav.pages', helpKey: 'overview.pagesHelp' },
  { section: 'menu', icon: 'menu_open', titleKey: 'nav.menus', helpKey: 'overview.menusHelp' },
  { section: 'elenchi', icon: 'view_list', titleKey: 'nav.listings', helpKey: 'overview.listingsHelp' },
  { section: 'tema', icon: 'palette', titleKey: 'nav.theme', helpKey: 'overview.themeHelp' },
  { section: 'sezioni', icon: 'widgets', titleKey: 'nav.sections', helpKey: 'overview.sectionsHelp' },
  { section: 'impostazioni', icon: 'settings', titleKey: 'nav.settings', helpKey: 'overview.settingsHelp' },
]

/** `/s/:siteId`: the state of one site at a glance, and the way to put it online. */
export function SiteOverviewScreen() {
  const { t, locale } = useI18n()
  const { site, siteId, projectNames } = useSiteWorkspace()
  const [counts, setCounts] = useState<Partial<Record<Status, number>> | null>(null)
  const [recent, setRecent] = useState<ContentSummary[] | null>(null)
  const [lastPublication, setLastPublication] = useState<PublicationSummary | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [types, setTypes] = useState<ContentTypeDefinition[]>([])
  useEffect(() => {
    let alive = true
    listContentTypes().then((next) => { if (alive) setTypes(next) }).catch(() => undefined)
    return () => { alive = false }
  }, [])
  const typeLabel = (key: string) => {
    const label = types.find((entry) => entry.key === key)?.label
    return label?.[locale] ?? label?.it ?? key
  }
  const now = Date.now()

  const loadPublication = useCallback(() => {
    listPublications(siteId, undefined, undefined, 1)
      .then((page) => setLastPublication(page.items[0] ?? null))
      .catch(() => setLastPublication(null))
  }, [siteId])

  useEffect(() => {
    let cancelled = false
    setCounts(null); setRecent(null); setLastPublication(undefined); setError(null)
    Promise.all([
      ...ACTIVE_STATUSES.map((status) => listContents({ siteId, status, limit: 1 })),
      listContents({ siteId, limit: 6 }),
    ]).then((results) => {
      if (cancelled) return
      setCounts(Object.fromEntries(ACTIVE_STATUSES.map((status, index) => [status, results[index]?.total ?? 0])))
      setRecent(results.at(-1)?.items ?? [])
    }).catch((err: unknown) => {
      if (!cancelled) setError(err instanceof ApiError ? err.message : t('errors.network'))
    })
    loadPublication()
    return () => { cancelled = true }
  }, [siteId, t, loadPublication])

  if (!site) return null
  const url = liveUrl(site.primaryDomain)

  return <div className="page">
    <PageHeader
      eyebrow={projectNames.get(site.projectId)}
      title={site.name}
      subtitle={<span className="inline-meta"><StatusChip tone={siteStatusTone(site.status)}>{t(`siteStatus.${site.status}`)}</StatusChip>{url ? <a href={url} target="_blank" rel="noopener noreferrer">{displayDomain(site.primaryDomain)}</a> : displayDomain(site.primaryDomain)}</span>}
      actions={<>
        {url && <ExternalButton href={url}>{t('live.openSite')}</ExternalButton>}
      </>}
    />

    {error && <Banner tone="error" title={t('dashboard.error')}>{error}</Banner>}

    <div className="stat-grid">
      {ACTIVE_STATUSES.map((status) => <Link key={status} className="card stat-card" to={paths.contents(siteId, { status })}>
        <span className={`stat-icon tone-${status}`}><Icon name={STATUS_ICONS[status]}/></span>
        <span className="stat-value">{counts ? counts[status] ?? 0 : <Skeleton width={48} height={32}/>}</span>
        <span className="stat-label">{t(`status.${status}Plural`)}</span>
      </Link>)}
      <div className="card stat-card stat-card-wide">
        <span className="stat-icon tone-info"><Icon name="rocket_launch"/></span>
        <span className="stat-label">{t('overview.online')}</span>
        {lastPublication === undefined ? <Skeleton width="70%"/>
          : lastPublication === null ? <span className="stat-text">{t('site.neverPublished')}</span>
          : <span className="stat-text">
            <StatusChip tone={publicationTone(lastPublication.status)}>{t(`publications.${lastPublication.status}`)}</StatusChip>
            <span title={absoluteDateTime(lastPublication.finishedAt ?? lastPublication.startedAt, locale)}>{t('overview.release', { release: lastPublication.releaseNumber, when: relativeTime(lastPublication.finishedAt ?? lastPublication.startedAt, now, locale) })}</span>
          </span>}
        <Link className="stat-link" to={paths.publications(siteId)}>{t('overview.allPublications')}</Link>
      </div>
    </div>

    <Banner tone="info" title={t('overview.publishHintTitle')}>{t('overview.publishHint')}</Banner>

    <Card title={t('dashboard.recent')} subtitle={t('dashboard.recentHelp')} icon="history" actions={<>
      <LinkButton variant="text" to={paths.contents(siteId)}>{t('dashboard.openAll')}</LinkButton>
    </>}>
      {recent === null ? <TableSkeleton rows={4} columns={3} label={t('common.loading')}/>
        : recent.length === 0 ? <EmptyState compact icon="article" title={t('list.emptyTitle')}>{t('overview.noContentHelp')}</EmptyState>
        : <ul className="list">{recent.map((item) => <li key={item.id}>
          <Link className="list-row" to={paths.content(siteId, item.id)}>
            <span className="list-row-icon"><Icon name={contentTypeIcon(item.contentTypeKey)}/></span>
            <span className="list-row-text"><strong>{item.title || item.slug}</strong><small>{typeLabel(item.contentTypeKey)} · {item.locale.toUpperCase()}</small></span>
            <StatusChip tone={contentStatusTone(item.status)}>{t(`status.${item.status}`)}</StatusChip>
            <time className="list-row-meta" title={absoluteDateTime(item.updatedAt, locale)}>{relativeTime(item.updatedAt, now, locale)}</time>
          </Link>
        </li>)}</ul>}
    </Card>

    <section className="page-section" aria-labelledby="overview-shortcuts">
      <h2 className="section-title" id="overview-shortcuts">{t('overview.manage')}</h2>
      <div className="card-grid">
        {SHORTCUTS.map((item) => <Link key={item.section} className="card shortcut-card" to={paths.siteSection(siteId, item.section)}>
          <span className="card-icon"><Icon name={item.icon}/></span>
          <span><strong>{t(item.titleKey)}</strong><small>{t(item.helpKey)}</small></span>
          <Icon name="chevron_right" className="shortcut-arrow"/>
        </Link>)}
      </div>
    </section>

  </div>
}
